import { ProviderContext, VariantType } from 'notistack';
import { transl } from '../main/transl';
import { mg } from '../mg/mg';
import { IOcResponse } from './CommSvc';

interface IToasterOptions {
	message?: string
	key?: string
}

class ToasterSvc {
	private worker: ProviderContext | undefined;

	init = (worker: ProviderContext) => void(this.worker = worker)

	_show = (variant: VariantType, key: string, options?: IToasterOptions) => {
		const message = options?.message || transl(key);
		if (!this.worker) return console.log('Cannot serve snack:', variant, key);
		this.worker.enqueueSnackbar(message, {preventDuplicate: true, autoHideDuration: 3000, ...options, variant, key});
	}

	hide = (key: string) => this.worker?.closeSnackbar(key)

	success = (key: string, options?: IToasterOptions) => this._show('success', key, options)

	info = (key: string, options?: IToasterOptions) => this._show('info', key, options)

	warning = (key: string, options?: IToasterOptions) => this._show('warning', key, options)

	error = (key: string, options?: IToasterOptions) => this._show('error', key, options)

	/** @deprecated Use handleResponse instead. */
	failure = (response: IOcResponse, options?: IToasterOptions) : Promise<IOcResponse> => {
		const f = response.failure;
		if (f) {
			if (f.type==='OFFLINE') {
				this.error('http.offline', options);
			} else {
				this.error('', {...options, message: f.translation.body});
			}
		} else if (!response.outputList) {
			console.error('Unknown failure', mg.stringify(response));
		}
		return Promise.resolve(response);
	}

	/**
	 * Return a function which:
	 * If there's a failure, reports it.
	 * Otherwise, if there's no output list, no blob and no etag, reports an 'unknownFailure'.
	 * Otherwise, if there's a non-empty successMessage, shows it.
	 * Otherwise, does nothing.
	 *
	 * Directly usable as commSvc.doPost(...).then(toasterSvc.handleResponse('It works!')).
	 *
	 * @returns The given response in a Promise.
	 */
	handleResponse = (successMessage: string) => ((response: IOcResponse) : Promise<IOcResponse> => {
		const f = response.failure;
		if (f) {
			if (f.type==='OFFLINE') {
				this.error('http.offline');
			} else {
				this.error(f.translation.body, {message: f.translation.body});
			}
		} else if (!response.outputList && !response.etag && !response.blob) {
			this.error('unknownFailure');
			console.error('Unknown failure', mg.stringify(response));
		} else if (successMessage) {
			this.success(successMessage);
		}
		return Promise.resolve(response);
	});
}

export const toasterSvc = new ToasterSvc();
